import {
  ACTOR_AIR_ACCEL,
  ACTOR_FUEL_BURN,
  ACTOR_FUEL_REGEN,
  ACTOR_GROUND_ACCEL,
  ACTOR_H,
  ACTOR_JET_ACCEL,
  ACTOR_JUMP_SPEED,
  ACTOR_MAX_FALL,
  ACTOR_MAX_FUEL,
  ACTOR_MAX_RISE,
  ACTOR_RUN_SPEED,
  ACTOR_STEP_UP,
  ACTOR_W,
  GRAVITY,
} from './constants.ts';
import type { Terrain } from './terrain.ts';
import { CLASSES, ClassId } from './body.ts';
import { FACTIONS } from './factions.ts';

export const BTN_LEFT = 1;
export const BTN_RIGHT = 2;
export const BTN_UP = 4;
export const BTN_DOWN = 8;
export const BTN_FIRE = 16;
/** Hold to aim down the scope: the view pushes out along the barrel. */
export const BTN_SCOPE = 32;
export const BTN_RELOAD = 64;
/**
 * The aim is locked onto a target by the aim assist (or the scope's lock):
 * the shooter holds the muzzle on it, so shots go down the sight line (no
 * recoil climb, and braced: half the spread, as when scoped).
 */
export const BTN_LOCK = 128;

/**
 * Stance: hold down to crouch; keep holding on the ground to go prone.
 * The hitbox shrinks from the top (the feet stay put) and the torso pivots
 * forward at the hip; moving is slower the lower you are.
 */
export const Stance = { Stand: 0, Crouch: 1, Prone: 2 } as const;
/** Hitbox height per stance. */
export const STANCE_H = [ACTOR_H, 10, 6];
/** Torso lean per stance (radians, toward where the clone faces). */
export const STANCE_LEAN = [0, 0.6, 1.4];
/** How far the hip sinks per stance (cells). */
export const STANCE_DROP = [0, 2, 1];
const STANCE_RUN = [1, 0.5, 0.28];
/** Ticks of holding down (on the ground) before a crouch drops prone. */
export const PRONE_TICKS = 12;
/** Hip pivot (from the hitbox's top-left, standing) and hip-to-shoulder length. */
export const HIP_X = 4;
export const HIP_Y = 9;
const TORSO = 5;
/** Crouching on the jetpack angles the thrust this far off vertical (a dash), up to this speed. */
const DASH_ANGLE = 0.95;
const DASH_MAX = 250;

/** World position of the shoulder (where aim is measured and shots leave from) for a stance and facing. */
export function shoulderAt(x: number, y: number, stance: number, faceLeft: boolean, out: { x: number; y: number }): { x: number; y: number } {
  const lean = STANCE_LEAN[stance] ?? 0;
  out.x = x + HIP_X + Math.sin(lean) * TORSO * (faceLeft ? -1 : 1);
  out.y = y + HIP_Y + (STANCE_DROP[stance] ?? 0) - Math.cos(lean) * TORSO;
  return out;
}

/** The part of an actor that client-side prediction replays. */
export interface Body {
  x: number; // top-left, world cells
  y: number;
  vx: number;
  vy: number;
  fuel: number;
  onGround: boolean;
  jetting: boolean;
  /** Legs still attached (0..2): two run, one hobbles, none crawls. */
  legs: number;
  /** Jetpack still attached. */
  jet: boolean;
  /** Clone class (body.ts): scales run speed, jetpack thrust and fuel use. */
  cls: number;
  /** Faction (factions.ts): scales run speed, jetpack thrust and fuel use on top of the class. */
  faction: number;
  /** Stance (Stance.*), and how long down has been held. */
  stance: number;
  downTicks: number;
  /** Carrying Extraction's golden idol: it's heavy (a slower run, a thirstier jetpack). */
  burdened?: boolean;
  /** A climber (the spider droid) clinging to a wall this tick: -1 on its left, 1 on its right, 0 not. */
  cling?: number;
  /** Possessed (a coral crab in it): with no legs left it doesn't stop, it crawls at you, flat and fast (CRAWL_RUN). */
  crawl?: boolean;
}

/** A climber's (the droid's) climb up a wall it's pushing against (cells/s), and the steps it takes in its stride. */
export const CLIMB_SPEED = 120;
const CLIMB_STEP_UP = ACTOR_STEP_UP + 5;
/** How far a wall can be for a climber to reach over and grab it (holding up). */
const CLIMB_REACH = 6;

/** What carrying the golden idol costs: run speed and jetpack fuel (thrust is untouched, so anyone can still climb). */
export const IDOL_BURDEN = { run: 0.8, fuel: 1.5 };
const NO_BURDEN = { run: 1, fuel: 1 };

export function newBody(x: number, y: number): Body {
  return { x, y, vx: 0, vy: 0, fuel: ACTOR_MAX_FUEL, onGround: false, jetting: false, legs: 2, jet: true, cls: ClassId.Medium, faction: 0, stance: Stance.Stand, downTicks: 0 };
}

export function copyBody(dst: Body, src: Body): void {
  dst.x = src.x;
  dst.y = src.y;
  dst.vx = src.vx;
  dst.vy = src.vy;
  dst.fuel = src.fuel;
  dst.onGround = src.onGround;
  dst.jetting = src.jetting;
  dst.legs = src.legs;
  dst.jet = src.jet;
  dst.burdened = src.burdened;
  dst.cls = src.cls;
  dst.faction = src.faction;
  dst.stance = src.stance;
  dst.downTicks = src.downTicks;
  dst.cling = src.cling;
  dst.crawl = src.crawl;
}

/**
 * The spider droid's leap and glide: it springs DROID_JUMP times a clone's
 * jump (about six bodies high), and falling with up held, its leg thrusters
 * ease it down (under its weight: they can't lift it) to DROID_GLIDE_FALL,
 * steering out wider than it runs, on the jetpack fuel it otherwise has no
 * use for.
 */
const DROID_JUMP = 1.55;
const DROID_GLIDE_THRUST = GRAVITY * 0.78;
const DROID_GLIDE_FALL = 50;
const DROID_GLIDE_RUN = 1.35;
const DROID_GLIDE_BURN = 16; // fuel a second
/** Run speed and jump strength by legs attached (0, 1, 2). */
const LEG_SPEED = [0.25, 0.55, 1];
const LEG_JUMP = [0, 0.7, 1];
/** A possessed clone with no legs, crawling (Body.crawl): its run speed, all told (most of a run, flat on its belly: it scuttles). */
export const CRAWL_RUN = 0.7;

/** Does the hitbox at (x, y), `h` tall from the feet up, overlap terrain? */
function collides(t: Terrain, x: number, y: number, h = ACTOR_H): boolean {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  return t.rectSolid(ix, iy + ACTOR_H - h, ix + ACTOR_W - 1, iy + ACTOR_H - 1);
}

/**
 * Advance one body by one fixed tick. Shared verbatim by the authoritative
 * server and the client's predictor, so given the same terrain and inputs the
 * results are identical. Returns the downward impact speed if the body landed
 * this tick (for fall damage), else 0.
 */
/** In water: what fraction of gravity it floats off, the push of a swimming stroke (cells/s²), and the velocity kept per tick (drag). */
export const WATER_LIFT = 0.92;
export const WATER_SWIM = 420;
export const WATER_DRAG = 0.9;

/** The digger's pull (cells/s²): running, it draws the clone a little toward where it's aimed. */
export const DIG_PULL = 240;

/** `pull`: the digger's aim while it runs (its pull, DIG_PULL), NaN when it isn't. */
export function stepBody(b: Body, buttons: number, t: Terrain, dt: number, pull = NaN): number {
  // Stance: down crouches; held on the ground it goes prone. Standing back
  // up (or just rising a level) needs the headroom for it.
  const base = CLASSES[b.cls] ?? CLASSES[ClassId.Medium];
  // (A droid never crouches or lies down: it's the shape it is.)
  const down = (buttons & BTN_DOWN) !== 0 && !base.climb;
  b.downTicks = down ? Math.min(PRONE_TICKS, b.downTicks + 1) : 0;
  // (Possessed and legless: flat on the ground, always.)
  const crawling = !!b.crawl && b.legs === 0;
  const want: number = crawling ? (b.onGround ? Stance.Prone : Stance.Crouch) : !down ? Stance.Stand : b.onGround && b.downTicks >= PRONE_TICKS ? Stance.Prone : Stance.Crouch;
  if (want > b.stance) b.stance = want;
  else {
    while (b.stance > want && !collides(t, b.x, b.y, STANCE_H[b.stance - 1])) b.stance--;
    if (b.stance === Stance.Prone && !b.onGround && !collides(t, b.x, b.y, STANCE_H[Stance.Crouch])) b.stance = Stance.Crouch;
  }
  const h = STANCE_H[b.stance];

  // Unstick: terrain may have been deposited on top of us.
  if (collides(t, b.x, b.y, h)) {
    for (let s = 1; s <= 12; s++) {
      if (!collides(t, b.x, b.y - s, h)) {
        b.y -= s;
        break;
      }
    }
  }

  const fac = FACTIONS[b.faction] ?? FACTIONS[0];
  const load = b.burdened ? IDOL_BURDEN : NO_BURDEN;
  const cls = { run: base.run * fac.run * load.run, jet: base.jet * fac.jet, fuel: base.fuel * fac.fuel * load.fuel };
  const dir = (buttons & BTN_RIGHT ? 1 : 0) - (buttons & BTN_LEFT ? 1 : 0);
  // Crouched on a burning jetpack in the air: the thrust swings forward into a dash.
  const dashDir = dir !== 0 ? dir : Math.sign(b.vx);
  const dashing = down && !b.onGround && buttons & BTN_UP && b.jet && b.fuel > 0 && dashDir !== 0;
  // A droid gliding: up held, falling, legs to steer by, fuel left (and not on a wall).
  // (Only one that's lost its jetpack: with it, it jets like anyone.)
  const gliding = !!base.climb && !b.jet && !b.onGround && (buttons & BTN_UP) !== 0 && b.vy > 0 && b.legs > 0 && b.fuel > 0 && !(b.cling ?? 0);
  if ((dir !== 0 || b.onGround) && !dashing) {
    const accel = (b.onGround ? ACTOR_GROUND_ACCEL : ACTOR_AIR_ACCEL) * dt;
    const legs = crawling ? CRAWL_RUN : LEG_SPEED[b.legs] * STANCE_RUN[b.stance];
    const dv = dir * ACTOR_RUN_SPEED * legs * cls.run * (gliding ? DROID_GLIDE_RUN : 1) - b.vx;
    b.vx += dv > accel ? accel : dv < -accel ? -accel : dv;
  }

  b.jetting = false;
  if (buttons & BTN_UP) {
    if (b.onGround && b.legs > 0 && b.stance !== Stance.Prone) {
      b.vy = -ACTOR_JUMP_SPEED * LEG_JUMP[b.legs] * (base.climb ? DROID_JUMP : 1);
      b.onGround = false;
    } else if (gliding) {
      b.vy -= DROID_GLIDE_THRUST * dt;
      if (b.vy > DROID_GLIDE_FALL) b.vy = Math.max(DROID_GLIDE_FALL, b.vy - GRAVITY * 2 * dt);
      b.fuel = Math.max(0, b.fuel - DROID_GLIDE_BURN * dt);
      b.jetting = true;
    } else if (b.jet && b.fuel > 0) {
      const thrust = ACTOR_JET_ACCEL * cls.jet * dt;
      if (dashing) {
        b.vx += Math.sin(DASH_ANGLE) * thrust * dashDir;
        b.vy -= Math.cos(DASH_ANGLE) * thrust;
        if (Math.abs(b.vx) > DASH_MAX) b.vx = Math.sign(b.vx) * DASH_MAX;
      } else if (!base.climb || b.vy > -ACTOR_MAX_RISE) b.vy -= thrust; // (a droid's pack lifts it no faster than a clone's: only its spring outruns that)
      b.fuel = Math.max(0, b.fuel - ACTOR_FUEL_BURN * cls.fuel * dt);
      b.jetting = true;
    }
  } else if (b.onGround) {
    b.fuel = Math.min(ACTOR_MAX_FUEL, b.fuel + ACTOR_FUEL_REGEN * dt);
  }

  // The digger running: a slight pull toward where it's aimed. (On its feet,
  // not up off them: it keeps its footing, and its stride, digging ahead.)
  if (pull === pull) {
    b.vx += Math.cos(pull) * DIG_PULL * dt;
    if (!b.onGround || Math.sin(pull) > 0) b.vy += Math.sin(pull) * DIG_PULL * dt;
  }
  b.vy += GRAVITY * dt;
  // In water (water.ts): it floats, swims (up held strokes upward, no fuel
  // needed; the pack refills), and everything is slow.
  if (t.water && t.wet(Math.floor(b.x + 4), Math.floor(b.y + h * 0.55))) {
    b.vy -= GRAVITY * WATER_LIFT * dt;
    if (buttons & BTN_UP) b.vy -= WATER_SWIM * dt;
    const drag = Math.pow(WATER_DRAG, dt * 30);
    b.vx *= drag;
    b.vy *= drag;
    b.fuel = Math.min(ACTOR_MAX_FUEL, b.fuel + ACTOR_FUEL_REGEN * dt);
  }
  if (b.vy > ACTOR_MAX_FALL) b.vy = ACTOR_MAX_FALL;
  const maxRise = base.climb ? Math.max(ACTOR_MAX_RISE, ACTOR_JUMP_SPEED * DROID_JUMP) : ACTOR_MAX_RISE; // (a droid's spring outruns a jetpack)
  if (b.vy < -maxRise) b.vy = -maxRise;

  // Horizontal sweep in <=1 cell steps, auto-climbing small ledges (a
  // climber takes bigger ones, and grips on whatever it's touching).
  const climber = !!base.climb && b.legs > 0;
  const gripping = b.onGround || (climber && (b.cling ?? 0) !== 0);
  const stepUp = climber ? CLIMB_STEP_UP : ACTOR_STEP_UP;
  let blocked = 0;
  let rem = b.vx * dt;
  while (rem !== 0) {
    const step = rem > 1 ? 1 : rem < -1 ? -1 : rem;
    const nx = b.x + step;
    if (!collides(t, nx, b.y, h)) {
      b.x = nx;
    } else {
      let climbed = false;
      if (gripping && b.legs > 0) {
        for (let s = 1; s <= stepUp; s++) {
          if (!collides(t, nx, b.y - s, h)) {
            b.x = nx;
            b.y -= s;
            climbed = true;
            break;
          }
        }
      }
      if (!climbed) {
        b.vx = 0;
        blocked = Math.sign(step);
        break;
      }
    }
    rem -= step;
  }
  // A climber pushing into a wall walks straight up it (and clings there,
  // gravity or not), cresting over the top when its legs reach it.
  // Holding up (W) with a wall within reach either side does the same: it
  // steps over to the wall and climbs (up a shaft, say).
  b.cling = 0;
  if (climber) {
    let into = blocked !== 0 ? blocked : dir !== 0 && collides(t, b.x + dir, b.y, h) ? dir : 0;
    if (into !== dir) into = 0;
    if (into === 0 && buttons & BTN_UP && !b.onGround) {
      for (let k = 1; k <= CLIMB_REACH && into === 0; k++) {
        for (const side of dir !== 0 ? [dir, -dir] : [1, -1]) {
          if (collides(t, b.x + side * k, b.y, h)) {
            into = side;
            // Over to it (as far as it's free to go).
            for (let m = 1; m < k && !collides(t, b.x + side, b.y, h); m++) b.x += side;
            break;
          }
        }
      }
    }
    if (into !== 0) {
      b.cling = into;
      b.vy = -CLIMB_SPEED * (b.legs >= 2 ? 1 : 0.55);
    }
  }

  // Vertical sweep.
  let impact = 0;
  rem = b.vy * dt;
  while (rem !== 0) {
    const step = rem > 1 ? 1 : rem < -1 ? -1 : rem;
    if (!collides(t, b.x, b.y + step, h)) {
      b.y += step;
      rem -= step;
    } else {
      if (step > 0) impact = b.vy;
      b.vy = 0;
      break;
    }
  }
  if (b.y < -400) {
    b.y = -400;
    if (b.vy < 0) b.vy = 0;
  }
  b.onGround = collides(t, b.x, b.y + 1, h);
  return impact;
}
